// src/license/LicenseContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "../lib/supabaseClient";

const AUTOMATIC_PRO_ENTITLEMENTS = {
  agentic_reports: true,
  advanced_exports: true,
  requirements_manager: true,
  risk_register: true,
  ai_pm: true,
  max_projects: 999999,
};

const LicenseCtx = createContext({
  ok: true,
  plan: "pro",
  status: "automatic",
  seats: 0,
  expiresAt: null,
  entitlements: AUTOMATIC_PRO_ENTITLEMENTS,
  loading: false,
  error: null,
  // helpers (no-ops by default so callers don't crash before provider mounts)
  refresh: async () => {},
  activate: async (_code) => {},
  canCreateAnotherProject: async () => ({ ok: true, count: 0, limit: Infinity }),
  createProject: async (_args) => ({ data: null, error: new Error("uninitialized") }),
});

export function LicenseProvider({ children }) {
  const [state, setState] = useState({
    ok: true,
    plan: "pro",
    status: "automatic",
    seats: 0,
    expiresAt: null,
    entitlements: AUTOMATIC_PRO_ENTITLEMENTS,
    loading: false,
    error: null,
  });

  /** GET /license/status (auth with current Supabase session) */
  const refresh = useCallback(async () => {
    setState({
      ok: true,
      plan: "pro",
      status: "automatic",
      seats: 0,
      expiresAt: null,
      entitlements: AUTOMATIC_PRO_ENTITLEMENTS,
      loading: false,
      error: null,
    });
  }, []);

  /** POST /license/activate  { code } */
  const activate = useCallback(
    async (code) => {
      await refresh();
      return { ok: true, plan: "pro", status: "automatic", code };
    },
    [refresh]
  );

  /**
   * Count user's projects and check against entitlement.
   * Returns { ok, count, limit } where ok=true means they can create another.
   */
  const canCreateAnotherProject = useCallback(async () => {
    return { ok: true, count: 0, limit: AUTOMATIC_PRO_ENTITLEMENTS.max_projects };
  }, []);

  /**
   * Preferred secure creation path via RPC (requires you to define the SQL function):
   *   create_project(p_name text, p_owner uuid) returns projects
   * Falls back to direct insert if RPC is not present.
   */
  const createProject = useCallback(
    async ({ name }) => {
      if (!name || !String(name).trim()) {
        return { data: null, error: new Error("Enter a project name") };
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return { data: null, error: new Error("Not signed in") };

      // Enforce cap on the client before calling DB
      const cap = await canCreateAnotherProject();
      if (!cap.ok) {
        return {
          data: null,
          error: new Error(
            `You’ve reached your limit of ${cap.limit} projects on your current plan.`
          ),
        };
      }

      // Try RPC first (secure, server-enforced)
      let rpcErr = null;
      try {
        const { data, error } = await supabase.rpc("create_project", {
          p_name: name,
          p_owner: userId,
        });
        if (!error) return { data, error: null };
        rpcErr = error;
      } catch (e) {
        rpcErr = e;
      }

      // Fallback: direct insert (only if your RLS allows it)
      const { data, error } = await supabase
        .from("projects")
        .insert({ name, owner_id: userId })
        .select()
        .single();

      if (error) {
        // Surface the RPC error if it looked like a limit violation
        const msg = (rpcErr?.message || "").toUpperCase();
        if (msg.includes("PROJECT_LIMIT_REACHED")) {
          return {
            data: null,
            error: new Error(
              `You’ve reached your project limit (${cap.limit}). Upgrade to create more.`
            ),
          };
        }
        return { data: null, error };
      }

      return { data, error: null };
    },
    [canCreateAnotherProject]
  );

  // Initial load + one light retry if it starts as not ok
  useEffect(() => {
    let cancel = false;

    (async () => {
      await refresh();
      setTimeout(async () => {
        if (!cancel && !state.ok) await refresh();
      }, 3000);
    })();

    // Re-check on auth state changes (sign-in/out)
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      refresh();
    });

    return () => {
      cancel = true;
      sub.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      refresh,
      activate,
      canCreateAnotherProject,
      createProject,
    }),
    [state, refresh, activate, canCreateAnotherProject, createProject]
  );

  return <LicenseCtx.Provider value={value}>{children}</LicenseCtx.Provider>;
}

export const useLicense = () => useContext(LicenseCtx);

export function hasFeature(entitlements, key) {
  const v = entitlements?.[key];
  return typeof v === "boolean" ? v : Boolean(v);
}

export function Gate({ feature, children, fallback = null }) {
  return <>{children}</>;
}
